import React, { useState, useEffect, useMemo } from 'react';
import { draftService, Draft } from '../services/draftService';
import { cn } from '../lib/cn';
import { useT } from '../lib/i18n';
import { handlePostToX } from '../utils/handlePostToX';
import { useToast } from './Toast';

type FilterMode = 'all' | 'favorites';
type SortMode = 'newest' | 'oldest' | 'score-high' | 'score-low';

export const Drafts: React.FC = () => {
  const { t, locale } = useT();
  const { toast } = useToast();
  const [drafts, setDrafts] = useState<Draft[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTag, setSelectedTag] = useState<string | null>(null);
  const [filterMode, setFilterMode] = useState<FilterMode>('all');
  const [sortMode, setSortMode] = useState<SortMode>('newest');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [newTag, setNewTag] = useState('');
  const [showExportMenu, setShowExportMenu] = useState(false);

  // データ読み込み
  useEffect(() => {
    loadDrafts();
  }, []);

  const loadDrafts = () => {
    setDrafts(draftService.getDrafts());
  };

  // 全タグ取得
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const allTags = useMemo(() => draftService.getAllTags(), [drafts]);

  // 統計情報
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stats = useMemo(() => draftService.getStats(), [drafts]);

  // フィルター・検索・ソート
  const filteredDrafts = useMemo(() => {
    let result = drafts;

    // フィルター
    if (filterMode === 'favorites') {
      result = result.filter(d => d.isFavorite);
    }

    // タグフィルター
    if (selectedTag) {
      result = result.filter(d => d.tags.includes(selectedTag));
    }

    // 検索
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(d =>
        d.text.toLowerCase().includes(query) ||
        d.tags.some(t => t.toLowerCase().includes(query)) ||
        (d.notes && d.notes.toLowerCase().includes(query))
      );
    }

    // ソート
    switch (sortMode) {
      case 'newest':
        result.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        break;
      case 'oldest':
        result.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
        break;
      case 'score-high':
        result.sort((a, b) => b.score - a.score);
        break;
      case 'score-low':
        result.sort((a, b) => a.score - b.score);
        break;
    }

    return result;
  }, [drafts, filterMode, selectedTag, searchQuery, sortMode]);

  // お気に入りトグル
  const handleToggleFavorite = (id: string) => {
    draftService.toggleFavorite(id);
    loadDrafts();
  };

  // 編集開始
  const handleStartEdit = (draft: Draft) => {
    setEditingId(draft.id);
    setEditText(draft.text);
    setEditNotes(draft.notes || '');
  };

  // 編集保存
  const handleSaveEdit = () => {
    if (!editingId) return;
    draftService.updateDraft(editingId, { 
      text: editText, 
      notes: editNotes.trim() || undefined 
    });
    setEditingId(null);
    loadDrafts();
  };

  // 編集キャンセル
  const handleCancelEdit = () => {
    setEditingId(null);
    setEditText('');
    setEditNotes('');
  };

  // タグ追加
  const handleAddTag = (id: string) => {
    if (!newTag.trim()) return;
    draftService.addTag(id, newTag.trim());
    setNewTag('');
    loadDrafts();
  };

  // タグ削除
  const handleRemoveTag = (id: string, tag: string) => {
    draftService.removeTag(id, tag);
    loadDrafts();
  };

  // 削除
  const handleDelete = (id: string) => {
    if (window.confirm(t('drafts.confirmDelete'))) {
      draftService.deleteDraft(id);
      loadDrafts();
    }
  };

  // コピー
  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    // TODO: トースト通知
  };

  // エクスポート
  const handleExport = (format: 'json' | 'csv' | 'txt' | 'md') => {
    draftService.export(format);
    setShowExportMenu(false);
  };

  // インポート
  const handleImport = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target?.result as string;
      const result = draftService.importFromJSON(content);
      
      if (result.success) {
        toast(t('drafts.importSuccess', { count: result.imported }), 'success');
        loadDrafts();
      } else {
        toast(t('drafts.importError', { error: result.error || '' }), 'error');
      }
    };
    reader.readAsText(file);
    e.target.value = ''; // リセット
  };

  return (
    <div className="p-4 sm:p-6 md:p-8 max-w-[1400px] mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-text-primary mb-2">
              {t('drafts.title')}
            </h1>
            <p className="text-text-muted text-sm">
              {t('drafts.subtitle')}
            </p>
          </div>
          
          {/* Export Menu */}
          <div className="relative">
            <button
              onClick={() => setShowExportMenu(!showExportMenu)}
              className="px-4 py-2 bg-brand text-white rounded-lg hover:bg-brand/90 transition-all"
            >
              {t('drafts.exportImport')}
            </button>
            
            {showExportMenu && (
              <div className="absolute right-0 mt-2 w-48 bg-surface-2 border border-border rounded-lg shadow-lg z-10">
                <div className="p-2">
                  <p className="text-xs text-text-muted mb-2 px-2">{t('drafts.format')}</p>
                  <button
                    onClick={() => handleExport('json')}
                    className="w-full text-left px-3 py-2 text-sm text-text-primary hover:bg-brand/10 rounded"
                  >
                    JSON
                  </button>
                  <button
                    onClick={() => handleExport('csv')}
                    className="w-full text-left px-3 py-2 text-sm text-text-primary hover:bg-brand/10 rounded"
                  >
                    CSV
                  </button>
                  <button
                    onClick={() => handleExport('txt')}
                    className="w-full text-left px-3 py-2 text-sm text-text-primary hover:bg-brand/10 rounded"
                  >
                    Text
                  </button>
                  <button
                    onClick={() => handleExport('md')}
                    className="w-full text-left px-3 py-2 text-sm text-text-primary hover:bg-brand/10 rounded"
                  >
                    Markdown
                  </button>
                  
                  <div className="border-t border-border my-2"></div>
                  
                  <label className="w-full block px-3 py-2 text-sm text-text-primary hover:bg-brand/10 rounded cursor-pointer">
                    {t('drafts.jsonImport')}
                    <input
                      type="file"
                      accept=".json"
                      onChange={handleImport}
                      className="hidden"
                    />
                  </label>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          <div className="bg-surface-2 border border-border rounded-xl p-4">
            <p className="text-text-muted text-xs mb-1">{t('drafts.stats.total')}</p>
            <p className="text-2xl font-bold text-text-primary">{stats.total}</p>
          </div>
          <div className="bg-surface-2 border border-border rounded-xl p-4">
            <p className="text-text-muted text-xs mb-1">{t('drafts.stats.favorites')}</p>
            <p className="text-2xl font-bold text-warning">{stats.favorites}</p>
          </div>
          <div className="bg-surface-2 border border-border rounded-xl p-4">
            <p className="text-text-muted text-xs mb-1">{t('drafts.stats.avgScore')}</p>
            <p className="text-2xl font-bold text-brand">{(stats.avgScore * 100).toFixed(0)}</p>
          </div>
          <div className="bg-surface-2 border border-border rounded-xl p-4">
            <p className="text-text-muted text-xs mb-1">{t('drafts.stats.tags')}</p>
            <p className="text-2xl font-bold text-text-primary">{allTags.length}</p>
          </div>
        </div>

        {/* Filters & Search */}
        <div className="space-y-4">
          {/* Search */}
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('drafts.search')}
            className="w-full px-4 py-3 bg-surface-2 border border-border rounded-xl text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-brand"
          />

          {/* Filter Buttons */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setFilterMode('all')}
              className={cn(
                'px-4 py-2 rounded-lg text-sm font-medium transition-all',
                filterMode === 'all'
                  ? 'bg-brand text-white'
                  : 'bg-surface-2 border border-border text-text-primary hover:bg-brand/10'
              )}
            >
              {t('drafts.filter.all')} ({drafts.length})
            </button>
            <button
              onClick={() => setFilterMode('favorites')}
              className={cn(
                'px-4 py-2 rounded-lg text-sm font-medium transition-all',
                filterMode === 'favorites'
                  ? 'bg-warning text-white'
                  : 'bg-surface-2 border border-border text-text-primary hover:bg-warning/10'
              )}
            >
              {t('drafts.filter.favorites')} ({stats.favorites})
            </button>

            {/* Sort */}
            <select
              value={sortMode}
              onChange={(e) => setSortMode(e.target.value as SortMode)}
              className="px-4 py-2 bg-surface-2 border border-border rounded-lg text-text-primary text-sm focus:outline-none focus:ring-2 focus:ring-brand"
            >
              <option value="newest">{t('drafts.sort.newest')}</option>
              <option value="oldest">{t('drafts.sort.oldest')}</option>
              <option value="score-high">{t('drafts.sort.scoreHigh')}</option>
              <option value="score-low">{t('drafts.sort.scoreLow')}</option>
            </select>
          </div>

          {/* Tag Filter */}
          {allTags.length > 0 && (
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setSelectedTag(null)}
                className={cn(
                  'px-3 py-1 rounded-full text-xs font-medium transition-all',
                  !selectedTag
                    ? 'bg-brand text-white'
                    : 'bg-surface-2 border border-border text-text-muted hover:bg-brand/10'
                )}
              >
                {t('drafts.tags.all')}
              </button>
              {allTags.map(tag => (
                <button
                  key={tag}
                  onClick={() => setSelectedTag(tag === selectedTag ? null : tag)}
                  className={cn(
                    'px-3 py-1 rounded-full text-xs font-medium transition-all',
                    selectedTag === tag
                      ? 'bg-accent text-white'
                      : 'bg-surface-2 border border-border text-text-primary hover:bg-accent/10'
                  )}
                >
                  {tag}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Draft List */}
      {filteredDrafts.length === 0 ? (
        <div className="text-center py-16 text-text-muted">
          {searchQuery || selectedTag ? t('drafts.empty.search') : t('drafts.empty.default')}
        </div>
      ) : (
        <div className="space-y-4">
          {filteredDrafts.map((draft) => (
            <div
              key={draft.id}
              className="bg-surface-2 border border-border rounded-xl p-5 transition-all hover:border-border-hover hover:shadow-card-hover"
            >
              {editingId === draft.id ? (
                /* 編集モード */
                <div className="space-y-3">
                  <textarea
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    className="w-full min-h-[100px] p-3 bg-surface-1 border border-border rounded-lg text-text-primary resize-y"
                  />
                  <textarea
                    value={editNotes}
                    onChange={(e) => setEditNotes(e.target.value)}
                    placeholder={t('drafts.notesPlaceholder')}
                    className="w-full p-3 bg-surface-1 border border-border rounded-lg text-text-primary text-sm resize-y"
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={handleSaveEdit}
                      className="px-4 py-2 bg-success text-white rounded-lg hover:bg-success/90"
                    >
                      {t('common.save')}
                    </button>
                    <button
                      onClick={handleCancelEdit}
                      className="px-4 py-2 bg-surface-2 border border-border text-text-primary rounded-lg hover:bg-surface-1"
                    >
                      {t('common.cancel')}
                    </button>
                  </div>
                </div>
              ) : (
                /* 表示モード */
                <>
                  {/* Header */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleToggleFavorite(draft.id)}
                        className="text-xl transition-transform hover:scale-110"
                      >
                        {draft.isFavorite ? (
                          <svg className="w-5 h-5 text-warning" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
                        ) : (
                          <svg className="w-5 h-5 text-text-muted" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
                        )}
                      </button>
                      <span className={cn(
                        'px-2 py-1 rounded text-xs font-bold',
                        draft.score >= 0.8
                          ? 'bg-success/20 text-success'
                          : draft.score >= 0.6
                            ? 'bg-brand/20 text-brand'
                            : 'bg-text-muted/20 text-text-muted'
                      )}>
                        {(draft.score * 100).toFixed(0)}
                      </span>
                    </div>
                    
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleStartEdit(draft)}
                        className="text-text-muted hover:text-brand transition-colors"
                        title={t('common.edit')}
                      >
                        {t('common.edit')}
                      </button>
                      <button
                        onClick={() => handleCopy(draft.text)}
                        className="text-text-muted hover:text-brand transition-colors"
                        title={t('common.copy')}
                      >
                        {t('common.copy')}
                      </button>
                      <button
                        onClick={() => handlePostToX(draft.text, toast, t)}
                        className="text-text-muted hover:text-brand transition-colors"
                        title={t('common.postToX')}
                      >
                        {t('common.postToX')}
                      </button>
                      <button
                        onClick={() => handleDelete(draft.id)}
                        className="text-text-muted hover:text-danger transition-colors"
                        title={t('common.delete')}
                      >
                        {t('common.delete')}
                      </button>
                    </div>
                  </div>

                  {/* Content */}
                  <p className="text-text-primary text-base leading-relaxed whitespace-pre-wrap mb-3">
                    {draft.text}
                  </p>

                  {/* Notes */}
                  {draft.notes && (
                    <div className="bg-surface-1 border-l-4 border-accent px-3 py-2 mb-3">
                      <p className="text-text-muted text-sm">{draft.notes}</p>
                    </div>
                  )}

                  {/* Tags */}
                  <div className="flex flex-wrap gap-2 mb-3">
                    {draft.tags.map(tag => (
                      <span
                        key={tag}
                        className="px-2 py-1 bg-accent/10 text-accent text-xs rounded-full flex items-center gap-1"
                      >
                        {tag}
                        <button
                          onClick={() => handleRemoveTag(draft.id, tag)}
                          className="hover:text-danger"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                    <input
                      type="text"
                      value={newTag}
                      onChange={(e) => setNewTag(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          handleAddTag(draft.id);
                        }
                      }}
                      placeholder={t('drafts.tags.add')}
                      className="px-2 py-1 bg-transparent border border-dashed border-border rounded-full text-xs text-text-muted focus:outline-none focus:border-brand"
                      style={{ width: '100px' }}
                    />
                  </div>

                  {/* Meta */}
                  <div className="flex items-center gap-4 text-xs text-text-muted">
                    <span>{t('drafts.created')} {new Date(draft.createdAt).toLocaleString(locale === 'ja' ? 'ja-JP' : 'en-US')}</span>
                    <span>{draft.text.length} {t('common.chars')}</span>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
